import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
  Avatar,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import {
  Search,
  Clear,
  FilterAlt,
  TableView,
  ViewModule,
  LocalShipping,
  VolunteerActivism,
  Assessment,
  Inventory,
  LocationOn,
  Numbers,
  FolderOpen,
} from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import { useLocalizedData } from '../hooks/useLocalizedData';
import { useLanguage } from '../contexts/LanguageContext';

import {
  Checkroom,
  Bed,
  Dry,
  LocalGroceryStore,
  EmojiFoodBeverage,
  BabyChangingStation,
  CleaningServices,
  LocalGasStation,
  HealthAndSafety,
} from "@mui/icons-material";

import villageData from "../data/villages_aid_data_updated.json";
// const villageData = [
//   // Your data here...
// ];

const itemTranslations = {
  food_rations: { ar: "حصص غذائية", en: "Food Rations" },
  mattresses: { ar: "فرش", en: "Mattresses" },
  blankets: { ar: "حرامات", en: "Blankets" },
  pillows: { ar: "مخدات", en: "Pillows" },
  milk: { ar: "حليب", en: "Milk" },
  baby_diapers: { ar: "حفاضات اطفال", en: "Baby Diapers" },
  adult_diapers: { ar: "حفاضات", en: "Adult Diapers" },
  shampoo: { ar: "شامبو", en: "Shampoo" },
  diesel_liters: { ar: "مازوت ليتر", en: "Diesel in Liters" },
  towels: { ar: "مناشف", en: "Towels" },
};

const itemIcons = {
  food_rations: <LocalGroceryStore />,
  mattresses: <Bed />,
  blankets: <Checkroom />,
  pillows: <Bed />,
  milk: <EmojiFoodBeverage />,
  baby_diapers: <BabyChangingStation />,
  adult_diapers: <BabyChangingStation />,
  shampoo: <CleaningServices />,
  diesel_liters: <LocalGasStation />,
  towels: <Dry />,
};

const VillageDistributionTable = () => {
  const { t } = useTranslation();
  const { getLocalizedValue } = useLocalizedData();
  const { currentLanguage } = useLanguage();
  const [filteredData, setFilteredData] = useState(villageData);
  const [filters, setFilters] = useState({
    search: "",
    village: "",
    item: "",
  });
  const [viewMode, setViewMode] = useState("cards");
  const [activeTab, setActiveTab] = useState(0);
  const [filterLanguage, setFilterLanguage] = useState("ar");
  const [isLoading, setIsLoading] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      const result = villageData.filter((village) => {
        const searchField = filters.search.toLowerCase();
        const villageField = village.village_ar;

        const hasMatchingItem =
          filters.item === "" ||
          Object.entries(village.items).some(
            ([key, value]) => value > 0 && key === filters.item
          );

        return (
          (filters.search === "" || villageField.includes(searchField)) &&
          (filters.village === "" ||
            (filterLanguage === "ar"
              ? village.village_ar
              : village.village_en) === filters.village) &&
          hasMatchingItem
        );
      });
      setFilteredData(result);
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, filterLanguage]);

  const handleChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFilters({
      search: "",
      village: "",
      item: "",
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFilterLanguageChange = () => {
    setFilterLanguage(filterLanguage === "ar" ? "en" : "ar");
  };

  const uniqueVillages = [
    ...new Set(
      villageData.map((village) =>
        filterLanguage === "ar" ? village.village_ar : village.village_en
      )
    ),
  ];

  const uniqueItems = Object.entries(itemTranslations)
    .filter(([key]) => villageData.some((village) => village.items[key] > 0))
    .map(([key, value]) => ({
      key,
      label: filterLanguage === "ar" ? value.ar : value.en,
    }));

  const totalVillages = filteredData.length;
  const totalItems = filteredData.reduce((sum, village) => {
    return sum + Object.values(village.items).reduce((itemSum, count) => itemSum + count, 0);
  }, 0);

  const StatsCard = ({ title, value, icon, color }) => (
    <Card className="elegant-card" sx={{ height: '100%' }}>
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Avatar
          sx={{
            bgcolor: `${color}.main`,
            width: 56,
            height: 56,
            mx: 'auto',
            mb: 2,
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700, color: `${color}.main`, mb: 1 }}>
          {value.toLocaleString()}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box className="animate-fade-in-up">
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h2" 
          className="heading-primary"
          sx={{ 
            textAlign: 'center',
            mb: 2,
            background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t("South Council")}
        </Typography>
        <Typography 
          variant="h6" 
          className="text-elegant"
          sx={{ 
            textAlign: 'center',
            color: 'text.secondary',
            maxWidth: '800px',
            mx: 'auto',
            mb: 3
          }}
        >
          {t("Village Aid Distribution Report")}
        </Typography>

        {/* Information Requests Button */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            component="a"
            href="https://drive.google.com/drive/folders/1CYswTK0luNszI5hwudURy56Aa9XhTxL0?usp=drive_link"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<FolderOpen />}
            sx={{
              background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(12, 159, 193, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(12, 159, 193, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'white' }}>
                {t("Register of Information Requests")}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem', color: 'white' }}>
                {t("Official Responses from Administration")}
              </Typography>
            </Box>
          </Button>
        </Box>
        
        {/* South Council Context */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            maxWidth: '900px', 
            mx: 'auto',
            background: 'linear-gradient(135deg, rgba(12, 159, 193, 0.05) 0%, rgba(77, 182, 209, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'primary.light',
            borderRadius: 3
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: 'center',
              color: 'text.primary',
              lineHeight: 1.7,
              fontSize: '1.1rem',
              mb: 3
            }}
          >
            {t("south_description")}
          </Typography>
          
          {/* Statistics Reference Button */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/stats/south"
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #0c9fc1 0%, #4db6d1 100%)',
                color: 'white',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0 4px 12px rgba(12, 159, 193, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0a7a96 0%, #0c9fc1 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(12, 159, 193, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
              startIcon={<Assessment />}
            >
              {t("View Detailed Statistics")}
            </Button>
          </Box>
        </Paper>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4, justifyContent: 'center' }}>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <StatsCard
            title={t("Total Villages")}
            value={totalVillages}
            icon={<LocationOn />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <StatsCard
            title={t("Total Items")}
            value={totalItems}
            icon={<Inventory />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <StatsCard
            title={t("Item Types")}
            value={uniqueItems.length}
            icon={<Assessment />}
            color="info"
          />
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            <FilterAlt sx={{ verticalAlign: "middle", mr: 1 }} />
            {t("Filter Village Distributions")}
          </Typography>
            <Box>
              <Button
                onClick={() =>
                  setViewMode(viewMode === "cards" ? "table" : "cards")
                }
                variant="outlined"
                sx={{ mr: 2 }}
                startIcon={
                  viewMode === "cards" ? <TableView /> : <ViewModule />
                }
              >
                {viewMode === "cards" ? t("Table View") : t("Card View")}
              </Button>
              <Button
                onClick={handleReset}
                startIcon={<Clear />}
                variant="outlined"
              >
                {t("Clear Filters")}
              </Button>
            </Box>
          </Box>

          {/* Full-width search field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label={t("Search Villages")}
              variant="outlined"
              value={filters.search}
              onChange={(e) => handleChange("search", e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: "action.active", mr: 1 }} />,
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={handleFilterLanguageChange}
                      sx={{ minWidth: 0 }}
                    >
                      {filterLanguage === "ar" ? "عربي" : "English"}
                    </Button>
                  </InputAdornment>
                ),
                sx: {
                  height: "56px",
                  "& input": {
                    padding: "16.5px 14px",
                  },
                },
              }}
            />
          </Box>

          {/* Select fields in a row */}
          <Box sx={{ width: "100%" }}>
            <Grid
              container
              spacing={2}
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr",
                },
              }}
            >
              <Grid item>
                <FormControl fullWidth>
                  <InputLabel>{t("Village")}</InputLabel>
                  <Select
                    value={filters.village}
                    label={t("Village")}
                    onChange={(e) => handleChange("village", e.target.value)}
                    sx={{ height: "56px" }}
                  >
                    <MenuItem value="">{t("All Villages")}</MenuItem>
                    {uniqueVillages.map((village) => (
                      <MenuItem key={village} value={village}>
                        {village}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <FormControl fullWidth>
                  <InputLabel>{t("Item")}</InputLabel>
                  <Select
                    value={filters.item}
                    label={t("Item")}
                    onChange={(e) => handleChange("item", e.target.value)}
                    sx={{ height: "56px" }}
                  >
                    <MenuItem value="">{t("All Items")}</MenuItem>
                    {uniqueItems.map((item) => (
                      <MenuItem key={item.key} value={item.key}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 3 }}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={t("All Records")} />
        </Tabs>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : filteredData.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h5" color="text.secondary">
              {t("No villages match your filters")}
            </Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={handleReset}>
              {t("Clear Filters")}
            </Button>
          </Paper>
        ) : viewMode === "cards" ? (
          <Grid
            container
            spacing={3}
            sx={{ justifyContent: 'center' }}
          >
            {filteredData.map((village, index) => {
              const distributedItems = Object.entries(village.items)
                .filter(([_, value]) => value > 0)
                .map(([key, value]) => ({
                  key,
                  value,
                  ...itemTranslations[key],
                  icon: itemIcons[key],
                }));

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <Card className="elegant-card animate-fade-in-up" sx={{ height: "100%" }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getLocalizedValue(village, 'village')}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {distributedItems.map((item) => (
                          <Box
                            key={item.key}
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: "primary.main",
                                color: "white",
                                width: 32,
                                height: 32,
                                mr: 2,
                              }}
                            >
                              {item.icon}
                            </Avatar>
                            <Box>
                              <Typography variant="body1">
                                {currentLanguage === 'ar' ? item.ar : item.en}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {t("Quantity")}: {item.value}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <TableContainer component={Paper} elevation={3}>
            <Table
              sx={{ minWidth: 650 }}
              aria-label="village distribution table"
            >
              <TableHead>
                <TableRow sx={{ bgcolor: "primary.main" }}>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    {t("Village")}
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    {t("Distributed Items")}
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    {t("Quantities")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.map((village, index) => {
                  const distributedItems = Object.entries(village.items)
                    .filter(([_, value]) => value > 0)
                    .map(([key, value]) => ({
                      key,
                      value,
                      ...itemTranslations[key],
                    }));

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "action.hover",
                        },
                        "&:hover": { backgroundColor: "action.selected" },
                      }}
                    >
                      <TableCell>{getLocalizedValue(village, 'village')}</TableCell>
                      <TableCell>
                        {distributedItems.map((item) => (
                          <Box
                            key={item.key}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: "primary.light",
                                color: "primary.contrastText",
                                width: 24,
                                height: 24,
                                mr: 1,
                              }}
                            >
                              {itemIcons[item.key]}
                            </Avatar>
                            <span>
                              {currentLanguage === 'ar' ? item.ar : item.en}
                            </span>
                          </Box>
                        ))}
                      </TableCell>
                      <TableCell>
                        {distributedItems.map((item) => (
                          <Box key={item.key} sx={{ mb: 1 }}>
                            {item.value}
                          </Box>
                        ))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
    </Box>
  );
};

export default VillageDistributionTable;
